// src/services/assistant/salonContextService.ts
// Costruzione del contesto dati passato all'edge function salon-assistant.
// Usato dalla pagina Assistente e dal widget flottante: unica fonte di verità.
import { Goal } from '@/services/goals/goalsService';
import { EmployeeCompensation, monthlyCostFromCompensation } from '@/services/employees/employeeCompensationService';
import { ShopCost, monthlyizeCost } from '@/services/shop/shopCostsService';

export interface SalonTrends {
    avgMonthlyRevenue: number;
    avgMonthlyNewClients: number;
}

interface AnalyticsHelpers {
    getValidBookings: (b: any[]) => any[];
    calculateRevenue: (b: any[]) => number;
    getPerformanceById?: (id: string) => any;
}

export const computeSalonTrends = (
    bookings: any[],
    clients: any[],
    { getValidBookings, calculateRevenue }: AnalyticsHelpers,
): SalonTrends => {
    const MONTHS = 6;
    const now = new Date();
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - MONTHS);

    const valid = getValidBookings(bookings || []);
    const recent = valid.filter((b: any) => {
        const d = new Date(b.date);
        return d >= cutoff && d <= now;
    });
    const avgMonthlyRevenue = calculateRevenue(recent) / MONTHS;

    const recentClients = (clients || []).filter((c: any) => c.created_at && new Date(c.created_at) >= cutoff);
    const avgMonthlyNewClients = recentClients.length / MONTHS;

    return { avgMonthlyRevenue, avgMonthlyNewClients };
};

export const buildSalonContext = (params: {
    organization: any;
    bookings: any[];
    clients: any[];
    users: any[];
    busySlots: any[];
    goals: Goal[];
    comps: EmployeeCompensation[];
    costs: ShopCost[];
    trends: SalonTrends;
    helpers: AnalyticsHelpers;
}) => {
    const { organization, bookings, clients, users, busySlots, goals, comps, costs, trends, helpers } = params;
    const { getValidBookings, calculateRevenue, getPerformanceById } = helpers;

    const valid = getValidBookings(bookings || []);
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const todayStr = now.toISOString().split('T')[0];
    const monthBookings = valid.filter((b: any) => new Date(b.date) >= startMonth && new Date(b.date) <= now);
    const noShowMonth = (bookings || []).filter((b: any) => b.status === 'no_show' && new Date(b.date) >= startMonth).length;

    // Serie storica mensile (ultimi 6 mesi) — indispensabile per stime e proiezioni.
    const andamento = [];
    for (let i = 5; i >= 0; i--) {
        const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const label = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
        const mb = valid.filter((b: any) => {
            const d = new Date(b.date);
            return d >= start && d < end;
        });
        const nc = (clients || []).filter((c: any) => c.created_at && new Date(c.created_at) >= start && new Date(c.created_at) < end).length;
        andamento.push({ mese: label, fatturato_eur: Math.round(calculateRevenue(mb)), appuntamenti: mb.length, nuovi_clienti: nc });
    }

    const totalRev = andamento.reduce((s, m) => s + m.fatturato_eur, 0);
    const totalAppts = andamento.reduce((s, m) => s + m.appuntamenti, 0);
    const ticketMedio = totalAppts > 0 ? Math.round(totalRev / totalAppts) : 0;
    const luogo = [organization?.city, organization?.address].filter(Boolean).join(', ') || null;

    // ===== Economia: produzione per dipendente, compensi, costi fissi, margine =====
    const cutoff6 = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    const recent6 = valid.filter((b: any) => new Date(b.date) >= cutoff6);
    const activeUsers = (users || []).filter((u: any) => u.is_active !== false);

    const personaleDettaglio = activeUsers.map((u: any) => {
        const own = recent6.filter((b: any) => b.hairdresser_id === u.id);
        const produzioneMediaMensile = Math.round(calculateRevenue(own) / 6);
        const comp = comps.find((c) => c.hairdresser_id === u.id);
        const costoMensile = comp ? Math.round(monthlyCostFromCompensation(comp)) : null;
        return {
            nome: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
            produzione_media_mensile_eur: produzioneMediaMensile,
            costo_mensile_eur: costoMensile,
            margine_mensile_eur: costoMensile != null ? produzioneMediaMensile - costoMensile : null,
            stipendio_mensile_eur: comp?.salary_monthly ?? null,
            tredicesima: comp?.thirteenth ?? null,
            quattordicesima: comp?.fourteenth ?? null,
            tfr: comp?.tfr ?? null,
        };
    });

    const costiFissiMensili = Math.round(costs.reduce((s, c) => s + monthlyizeCost(c), 0));
    const compensiMensiliTotali = Math.round(comps.reduce((s, c) => s + monthlyCostFromCompensation(c), 0));
    const fatturatoMedioMensile = Math.round(trends.avgMonthlyRevenue);
    const margineMensileStimato = fatturatoMedioMensile - costiFissiMensili - compensiMensiliTotali;

    // ===== Clienti: pattern di visita (per domande sui singoli clienti) =====
    const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayStrLocal = ymd(now);
    const datesByClient = new Map<string, string[]>();
    const staffNameById = new Map<string, string>(
        (users || []).map((u: any) => [u.id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]),
    );
    // Visite di ogni cliente suddivise per barbiere (per "clienti affezionati di X")
    const visitsByClientByStaff = new Map<string, Map<string, number>>();
    for (const b of valid) {
        if (!b.client_id) continue;
        const arr = datesByClient.get(b.client_id) || [];
        arr.push(b.date);
        datesByClient.set(b.client_id, arr);
        const staffName = staffNameById.get(b.hairdresser_id);
        if (staffName) {
            const m = visitsByClientByStaff.get(b.client_id) || new Map<string, number>();
            m.set(staffName, (m.get(staffName) || 0) + 1);
            visitsByClientByStaff.set(b.client_id, m);
        }
    }
    const clientiDettaglio = (clients || []).map((c: any) => {
        const nome = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || 'Cliente';
        const raw = datesByClient.get(c.id) || [];
        const uniq = Array.from(new Set(raw)).sort();
        const intervals: number[] = [];
        for (let i = 1; i < uniq.length; i++) {
            intervals.push((new Date(uniq[i]).getTime() - new Date(uniq[i - 1]).getTime()) / 86400000);
        }
        const avg = intervals.length ? intervals.reduce((s, x) => s + x, 0) / intervals.length : null;
        const std = intervals.length ? Math.sqrt(intervals.reduce((s, x) => s + (x - (avg as number)) ** 2, 0) / intervals.length) : null;
        const pastDates = uniq.filter((d) => d <= todayStrLocal);
        const lastPast = pastDates.length ? pastDates[pastDates.length - 1] : null;
        const futureDates = uniq.filter((d) => d > todayStrLocal);
        // Barbiere abituale = quello con più visite di questo cliente
        const perStaff = visitsByClientByStaff.get(c.id);
        let barbiereAbituale: string | null = null;
        let visitePerBarbiere: Record<string, number> | null = null;
        if (perStaff && perStaff.size > 0) {
            visitePerBarbiere = Object.fromEntries(perStaff);
            barbiereAbituale = Array.from(perStaff.entries()).sort((a, b) => b[1] - a[1])[0][0];
        }
        return {
            nome,
            n_appuntamenti: raw.length,
            barbiere_abituale: barbiereAbituale,
            visite_per_barbiere: visitePerBarbiere,
            intervallo_medio_giorni: avg != null ? Math.round(avg) : null,
            irregolarita_giorni: std != null ? Math.round(std) : null, // deviazione std intervalli: alta = pattern meno costante
            ultima_visita: lastPast,
            giorni_da_ultima_visita: lastPast ? Math.round((now.getTime() - new Date(lastPast).getTime()) / 86400000) : null,
            prossimo_appuntamento: futureDates.length ? futureDates[0] : null,
        };
    });

    // ===== Occupazione / "buchi" per dipendente (prossimi 28 giorni) =====
    const weekdayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const toMin = (t: any): number | null => {
        if (!t) return null;
        const [h, m] = String(t).split(':').map(Number);
        return h * 60 + m;
    };
    const WINDOW_DAYS = 28;
    const winStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const winEnd = new Date(winStart);
    winEnd.setDate(winStart.getDate() + WINDOW_DAYS);
    const winStartStr = ymd(winStart);
    const winEndStr = ymd(winEnd);
    const occByUser = new Map<string, { avail: number; booked: number }>();
    activeUsers.forEach((u: any) => occByUser.set(u.id, { avail: 0, booked: 0 }));
    for (let i = 0; i < WINDOW_DAYS; i++) {
        const d = new Date(winStart);
        d.setDate(winStart.getDate() + i);
        const dow = weekdayNames[d.getDay()];
        const dateStr = ymd(d);
        const shopH = (organization as any)?.time_ranges?.[dow];
        if (!shopH?.open || !shopH?.close) continue;
        if ((busySlots || []).some((s: any) => s.hairdresser_id === null && s.type === 'holiday' && dateStr >= s.start_date && dateStr <= s.end_date)) continue;
        const shopOpen = toMin(shopH.open)!;
        const shopClose = toMin(shopH.close)!;
        activeUsers.forEach((u: any) => {
            const eh = u.time_ranges?.[dow];
            if (!eh?.open || !eh?.close) return;
            if ((busySlots || []).some((s: any) => s.hairdresser_id === u.id && s.type === 'holiday' && dateStr >= s.start_date && dateStr <= s.end_date)) return;
            const start = Math.max(toMin(eh.open)!, shopOpen);
            const end = Math.min(toMin(eh.close)!, shopClose);
            if (start >= end) return;
            let avail = end - start;
            if (eh.start_break && eh.end_break) {
                const bs = Math.max(toMin(eh.start_break)!, start);
                const be = Math.min(toMin(eh.end_break)!, end);
                if (bs < be) avail -= be - bs;
            }
            occByUser.get(u.id)!.avail += Math.max(0, avail);
        });
    }
    for (const b of valid) {
        if (!occByUser.has(b.hairdresser_id)) continue;
        if (b.date >= winStartStr && b.date < winEndStr) {
            occByUser.get(b.hairdresser_id)!.booked += getPerformanceById?.(b.performance_id)?.duration || 30;
        }
    }
    const occupazioneDipendenti = activeUsers.map((u: any) => {
        const o = occByUser.get(u.id)!;
        return {
            nome: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
            ore_disponibili: Math.round(o.avail / 60),
            ore_prenotate: Math.round(o.booked / 60),
            ore_libere: Math.round(Math.max(0, o.avail - o.booked) / 60), // "buchi": più alto = più tempo non prenotato
            occupazione_pct: o.avail > 0 ? Math.round((o.booked / o.avail) * 100) : null,
        };
    });

    return {
        salone: organization?.name ?? null,
        luogo,
        data_odierna: todayStr,
        ticket_medio_eur: ticketMedio,
        fatturato_medio_mensile_eur: Math.round(trends.avgMonthlyRevenue),
        fatturato_mese_corrente_eur: Math.round(calculateRevenue(monthBookings)),
        nuovi_clienti_medi_al_mese: Number(trends.avgMonthlyNewClients.toFixed(1)),
        clienti_totali: (clients || []).length,
        appuntamenti_oggi: valid.filter((b: any) => b.date === todayStr).length,
        appuntamenti_validi_totali: valid.length,
        no_show_mese_corrente: noShowMonth,
        andamento_mensile_ultimi_6_mesi: andamento,
        personale: activeUsers.map((u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()),
        // Economia dipendenti/salone (per margine, sostenibilità, break-even)
        personale_dettaglio: personaleDettaglio,
        costi_fissi_salone: costs.map((c) => ({
            nome: c.name,
            categoria: c.category,
            importo_eur: c.amount,
            ricorrenza: c.recurrence,
            equivalente_mensile_eur: Math.round(monthlyizeCost(c)),
        })),
        costi_fissi_mensili_totali_eur: costiFissiMensili,
        compensi_mensili_totali_eur: compensiMensiliTotali,
        margine_mensile_stimato_eur: margineMensileStimato,
        // Clienti del salone con pattern di visita (per domande sui singoli clienti)
        clienti: clientiDettaglio,
        // Occupazione agenda per dipendente sui prossimi 28 giorni ("buchi" = ore_libere)
        occupazione_dipendenti_prossimi_28gg: occupazioneDipendenti,
        obiettivi: goals.map((g) => ({ tipo: g.type, target: g.target, scadenza: g.deadline, titolo: g.title })),
    };
};
